import React, { useEffect, useState, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { useLanguage } from '../i18n/LanguageContext';
import SEO, { generateBreadcrumbSchema, generateArticleSchema } from '../components/SEO';
import { Button } from '../components/ui/button';
import { productImage } from '../utils/imageUtils';
import { getClusterForSlug } from '../utils/knowledgeClusters';
import {
  Clock, ChevronRight, ArrowLeft, Tag as TagIcon, Share2,
  ChevronUp, BookOpen, Calendar, Package, Calculator,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABELS_SV = {
  guide: 'Guider',
  'how-to': 'Steg-för-steg',
  news: 'Nyheter',
  faq: 'Vanliga frågor',
};

// Build a slugged ID for headings, used for TOC anchors
const slugify = (str) => (str || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

const KnowledgeArticlePage = () => {
  const { slug } = useParams();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [clusterArticles, setClusterArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const cluster = getClusterForSlug(slug);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/articles/${slug}`);
        setArticle(res.data);

        // Sibling articles in the same topic cluster
        const c = getClusterForSlug(slug);
        if (c) {
          const all = await axios.get(`${API}/articles`).then((r) => r.data).catch(() => []);
          setClusterArticles(all.filter((a) => c.slugs.includes(a.slug) && a.slug !== slug));
        }

        // Fetch related products if any
        if ((res.data.related_product_ids || []).length > 0) {
          const products = await Promise.all(
            res.data.related_product_ids.slice(0, 6).map((id) =>
              axios.get(`${API}/products/${id}`).then((r) => r.data).catch(() => null),
            ),
          );
          setRelatedProducts(products.filter(Boolean));
        }
      } catch (err) {
        if (err?.response?.status === 404) {
          navigate('/kunskap', { replace: true });
        }
      } finally {
        setLoading(false);
      }
    };
    if (slug) load();
  }, [slug, navigate]);

  // Add IDs to H2/H3 in rendered content for TOC anchoring
  const enrichedContent = useMemo(() => {
    if (!article) return '';
    const raw = language === 'sv' ? article.content_sv : (article.content_en || article.content_sv);
    if (!raw) return '';
    return raw.replace(/<(h[23])([^>]*)>([\s\S]*?)<\/\1>/gi, (full, tag, attrs, inner) => {
      const text = inner.replace(/<[^>]*>/g, '').trim();
      const id = slugify(text);
      // Don't double-add id
      if (/\bid\s*=/.test(attrs)) return full;
      return `<${tag} id="${id}"${attrs}>${inner}</${tag}>`;
    });
  }, [article, language]);

  // Extract TOC from content
  const toc = useMemo(() => {
    if (!enrichedContent) return [];
    const re = /<(h[23])[^>]*>([\s\S]*?)<\/\1>/gi;
    const items = [];
    let m;
    while ((m = re.exec(enrichedContent)) !== null) {
      const text = m[2].replace(/<[^>]*>/g, '').trim();
      if (text) {
        items.push({
          level: m[1] === 'h2' ? 2 : 3,
          text,
          id: slugify(text),
        });
      }
    }
    return items;
  }, [enrichedContent]);

  // Show back-to-top after scroll
  useEffect(() => {
    const onScroll = () => setShowBackToTop(window.scrollY > 600);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleShare = async () => {
    const url = window.location.href;
    const title = language === 'sv' ? article?.title_sv : article?.title_en;
    if (navigator.share) {
      try {
        await navigator.share({ title, url });
      } catch {
        // User cancelled
      }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        // eslint-disable-next-line no-alert
        alert(language === 'sv' ? 'Länk kopierad!' : 'Link copied!');
      } catch {
        // ignore
      }
    }
  };

  if (loading) {
    return (
      <div className="bg-[#FAFAF8] min-h-screen pt-32" data-testid="knowledge-article-loading">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="h-4 bg-stone-200 w-1/4 mb-6" />
            <div className="h-12 bg-stone-200 w-3/4 mb-4" />
            <div className="h-4 bg-stone-200 w-full mb-2" />
            <div className="h-4 bg-stone-200 w-5/6" />
          </div>
        </div>
      </div>
    );
  }

  if (!article) return null;

  const title = language === 'sv' ? article.title_sv : (article.title_en || article.title_sv);
  const excerpt = language === 'sv' ? article.excerpt_sv : (article.excerpt_en || article.excerpt_sv);
  const metaTitle = language === 'sv' ? article.meta_title_sv : article.meta_title_en;
  const metaDesc = language === 'sv' ? article.meta_description_sv : article.meta_description_en;

  const breadcrumbs = [
    { name: language === 'sv' ? 'Hem' : 'Home', url: 'https://flowingo.se/' },
    { name: language === 'sv' ? 'Kunskap' : 'Knowledge', url: 'https://flowingo.se/kunskap' },
    { name: title, url: `https://flowingo.se/kunskap/${article.slug}` },
  ];

  const publishedDate = article.published_at
    ? new Date(article.published_at).toLocaleDateString(language === 'sv' ? 'sv-SE' : 'en-GB', {
        year: 'numeric', month: 'long', day: 'numeric',
      })
    : '';

  return (
    <div className="bg-white min-h-screen" data-testid="knowledge-article-page">
      <SEO
        title={metaTitle || title}
        description={metaDesc || excerpt}
        type="article"
        image={article.cover_image_url}
        language={language}
        schema={[
          generateBreadcrumbSchema(breadcrumbs),
          generateArticleSchema(article, language),
        ].flat()}
      />

      {/* Article Hero */}
      <article className="pt-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
          {/* Breadcrumb */}
          <nav className="text-xs text-stone-500 flex items-center gap-1 mb-6 flex-wrap" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-[#321a27]">{language === 'sv' ? 'Hem' : 'Home'}</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/kunskap" className="hover:text-[#321a27]">{language === 'sv' ? 'Kunskap' : 'Knowledge'}</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-stone-700 truncate max-w-[40ch]">{title}</span>
          </nav>

          <Link
            to="/kunskap"
            className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-[#321a27] mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            {language === 'sv' ? 'Tillbaka till alla artiklar' : 'Back to all articles'}
          </Link>

          {/* Category + Reading time */}
          <div className="flex items-center gap-3 text-xs uppercase tracking-wider text-[#321a27] mb-4">
            <span className="font-semibold">
              {CATEGORY_LABELS_SV[article.category] || article.category}
            </span>
            <span className="text-stone-300">·</span>
            <span className="flex items-center gap-1 text-stone-500">
              <Clock className="w-3 h-3" />
              {article.reading_time_minutes || 5} {language === 'sv' ? 'min läsning' : 'min read'}
            </span>
          </div>

          {/* Title */}
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-stone-900 mb-6 leading-tight">
            {title}
          </h1>

          {excerpt && (
            <p className="text-lg text-stone-600 mb-8 leading-relaxed">{excerpt}</p>
          )}

          {/* Author / date / share */}
          <div className="flex flex-wrap items-center gap-4 pb-8 border-b border-stone-200 text-sm text-stone-500">
            <span className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#321a27] text-white rounded-full flex items-center justify-center text-xs font-bold">
                {(article.author || 'R').charAt(0)}
              </div>
              {article.author || (language === 'sv' ? 'Rafid Kadhim, Försäljningsansvarig' : 'Rafid Kadhim, Head of Sales')}
            </span>
            {publishedDate && (
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" /> {publishedDate}
              </span>
            )}
            <button
              type="button"
              onClick={handleShare}
              data-testid="article-share-btn"
              className="ml-auto flex items-center gap-1 text-stone-500 hover:text-[#321a27] transition-colors"
            >
              <Share2 className="w-4 h-4" />
              {language === 'sv' ? 'Dela' : 'Share'}
            </button>
          </div>
        </div>

        {/* Cover image */}
        {article.cover_image_url && (
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
            <img
              src={article.cover_image_url}
              alt={title}
              className="w-full aspect-video object-cover bg-stone-100"
              loading="eager"
            />
          </div>
        )}

        {/* Body + TOC */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-12 pb-16">
          {/* Content */}
          <div className="max-w-3xl">
            <div
              className="prose prose-stone prose-lg max-w-none article-content"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(enrichedContent) }}
              data-testid="article-content"
            />

            {/* Tags */}
            {(article.tags || []).length > 0 && (
              <div className="mt-12 pt-8 border-t border-stone-200">
                <p className="text-xs uppercase tracking-wider text-stone-500 mb-3">
                  {language === 'sv' ? 'Taggar' : 'Tags'}
                </p>
                <div className="flex flex-wrap gap-2">
                  {article.tags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/kunskap?tag=${encodeURIComponent(tag)}`}
                      className="text-xs px-3 py-1.5 bg-stone-100 text-stone-600 hover:bg-[#321a27] hover:text-white transition-colors inline-flex items-center gap-1"
                    >
                      <TagIcon className="w-3 h-3" /> {tag}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Topic cluster: sibling articles + tools */}
            {cluster && (
              <div className="mt-12 pt-8 border-t border-stone-200" data-testid="cluster-related-section">
                <h2 className="text-2xl font-bold text-stone-900 mb-2">
                  {language === 'sv' ? `Mer i ämnet: ${cluster.title}` : `More on: ${cluster.title}`}
                </h2>
                <p className="text-stone-600 mb-6">
                  {language === 'sv'
                    ? <>Del av vår <Link to="/kunskap" className="text-[#321a27] underline hover:no-underline">kompletta guide till dimensionering av VVS-system</Link>.</>
                    : <>Part of our <Link to="/kunskap" className="text-[#321a27] underline hover:no-underline">complete HVAC sizing guide</Link>.</>}
                </p>
                {clusterArticles.length > 0 && (
                  <ul className="space-y-2 mb-6">
                    {clusterArticles.map((a) => (
                      <li key={a.slug}>
                        <Link
                          to={`/kunskap/${a.slug}`}
                          className="text-[#321a27] underline hover:no-underline"
                          data-testid={`cluster-sibling-${a.slug}`}
                        >
                          {language === 'sv' ? a.title_sv : (a.title_en || a.title_sv)}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="flex flex-wrap gap-2">
                  {(cluster.calculators || []).map((calc) => (
                    <Link
                      key={calc.href}
                      to={calc.href}
                      className="inline-flex items-center gap-2 text-xs font-medium bg-[#321a27] text-white px-3 py-2 hover:bg-[#4a2d3d] transition-colors"
                    >
                      <Calculator className="w-3.5 h-3.5" /> {calc.name}
                    </Link>
                  ))}
                  <Link
                    to={cluster.category.href}
                    className="inline-flex items-center gap-2 text-xs font-medium border border-stone-300 text-stone-700 px-3 py-2 hover:border-[#321a27] hover:text-[#321a27] transition-colors"
                    data-testid="cluster-category-link"
                  >
                    <Package className="w-3.5 h-3.5" /> {cluster.category.name}
                  </Link>
                </div>
              </div>
            )}

            {/* Related Products */}
            {relatedProducts.length > 0 && (
              <div className="mt-12 pt-8 border-t border-stone-200">
                <h2 className="text-2xl font-bold text-stone-900 mb-2">
                  {language === 'sv' ? 'Relevanta produkter' : 'Related products'}
                </h2>
                <p className="text-stone-600 mb-6">
                  {language === 'sv'
                    ? 'Produkter som passar för det du läst om i den här artikeln.'
                    : 'Products matching what you read about in this article.'}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {relatedProducts.map((p) => (
                    <Link
                      key={p.id}
                      to={`/produkter/${p.slug || p.id}`}
                      className="bg-stone-50 border border-stone-200 p-4 hover:border-[#321a27] transition-colors flex gap-4 group"
                    >
                      <div className="w-20 h-20 bg-white border border-stone-200 flex-shrink-0 flex items-center justify-center overflow-hidden">
                        {p.image_url ? (
                          <img src={productImage(p.image_url)} alt={p.name_sv} className="w-full h-full object-contain p-1" />
                        ) : (
                          <Package className="w-8 h-8 text-stone-300" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-stone-900 group-hover:text-[#321a27] truncate">
                          {language === 'sv' ? p.name_sv : (p.name_en || p.name_sv)}
                        </p>
                        <p className="text-xs text-stone-500 line-clamp-2 mt-1">
                          {(language === 'sv' ? p.description_sv : p.description_en)?.replace(/<[^>]*>/g, '').slice(0, 80)}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* CTA */}
            <div className="mt-12 bg-gradient-to-br from-[#321a27] to-[#4a2d3d] text-white p-8">
              <BookOpen className="w-8 h-8 mb-4 opacity-80" />
              <h3 className="text-xl font-bold mb-2">
                {language === 'sv' ? 'Behöver du hjälp med ditt projekt?' : 'Need help with your project?'}
              </h3>
              <p className="text-white/80 mb-5">
                {language === 'sv'
                  ? 'Våra ingenjörer ger gratis teknisk rådgivning. Boka ett samtal eller skicka en offertförfrågan.'
                  : 'Our engineers provide free technical advice. Book a call or send a quote request.'}
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/kontakt">
                  <Button className="bg-white text-[#321a27] hover:bg-stone-100 rounded-none">
                    {language === 'sv' ? 'Kontakta oss' : 'Contact us'}
                  </Button>
                </Link>
                <Link to="/snabboffert">
                  <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 rounded-none">
                    {language === 'sv' ? 'Begär offert' : 'Request quote'}
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          {/* TOC sidebar */}
          {toc.length > 0 && (
            <aside className="hidden lg:block">
              <div className="sticky top-28">
                <p className="text-xs uppercase tracking-wider text-stone-500 mb-4 font-semibold">
                  {language === 'sv' ? 'I den här artikeln' : 'In this article'}
                </p>
                <nav className="space-y-1 text-sm">
                  {toc.map((item, idx) => (
                    <a
                      key={`${item.id}-${idx}`}
                      href={`#${item.id}`}
                      className={`block hover:text-[#321a27] transition-colors py-1 ${
                        item.level === 3 ? 'pl-4 text-stone-500' : 'text-stone-700 font-medium'
                      }`}
                    >
                      {item.text}
                    </a>
                  ))}
                </nav>
              </div>
            </aside>
          )}
        </div>
      </article>

      {/* Back to top */}
      {showBackToTop && (
        <button
          type="button"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-6 right-6 w-12 h-12 bg-[#321a27] text-white shadow-lg hover:bg-[#4a2d3d] transition-colors flex items-center justify-center z-40"
          aria-label="Back to top"
        >
          <ChevronUp className="w-5 h-5" />
        </button>
      )}
    </div>
  );
};

export default KnowledgeArticlePage;
